import {
  FLEET_BILLING_CYCLE_LABELS,
  type FleetBillingCycle,
  type FleetPortalVehicle,
} from "@station/contracts";
import {
  Alert,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  MoneyText,
  QuantityText,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Car } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPlateDisplay } from "../../features/customers/personal-data-values.js";
import { litresText, moneyText } from "../../features/fleet/fleet-values.js";
import { Field } from "../../features/market/shared.js";
import { usePortal } from "../../features/portal/portal-context.js";
import { usePortalSummary } from "../../features/portal/queries.js";
import { PortalHeader, UsageMeter } from "../../features/portal/shared.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount, formatDate } from "../../lib/format.js";

export const Route = createFileRoute("/portal/")({ component: PortalSummaryRoute });

/**
 * Filo portalı özeti (ADR 0017 §17): cari kredisi (şirket başına limit / bakiye / kullanılabilir), filonun bugün / bu ay
 * kullanımı ve ARAÇ BAŞINA bugün / bu ay kullanım ↔ etkin kural limitleri. Plaka filonun kendi verisidir (açık).
 */
function PortalSummaryRoute(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  const summary = usePortalSummary(portal.counterpartyId);

  const columns = useMemo<DataTableColumn<FleetPortalVehicle>[]>(
    () => [
      {
        id: "plate",
        header: t("portal.fields.plate"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/portal/islemler"
            search={{ vehicle: row.vehicleId }}
            className="font-mono underline underline-offset-4"
          >
            {formatPlateDisplay(row.plate)}
            <span className="sr-only"> — {t("portal.vehicles.transactions")}</span>
          </Link>
        ),
      },
      {
        id: "dayLitres",
        header: t("portal.fields.dayLitres"),
        cell: (row) => (
          <UsageMeter
            used={row.usage.dayLitres}
            limit={row.policy.dailyLitreLimit}
            format={litresText}
            noLimit={t("portal.noLimit")}
          />
        ),
      },
      {
        id: "monthLitres",
        header: t("portal.fields.monthLitres"),
        cell: (row) => (
          <UsageMeter
            used={row.usage.monthLitres}
            limit={row.policy.monthlyLitreLimit}
            format={litresText}
            noLimit={t("portal.noLimit")}
          />
        ),
      },
      {
        id: "dayAmount",
        header: t("portal.fields.dayAmount"),
        cell: (row) => (
          <UsageMeter
            used={row.usage.dayAmount}
            limit={row.policy.dailyAmountLimit}
            format={moneyText}
            noLimit={t("portal.noLimit")}
          />
        ),
      },
      {
        id: "monthAmount",
        header: t("portal.fields.monthAmount"),
        cell: (row) => (
          <UsageMeter
            used={row.usage.monthAmount}
            limit={row.policy.monthlyAmountLimit}
            format={moneyText}
            noLimit={t("portal.noLimit")}
          />
        ),
      },
      {
        id: "odometer",
        header: t("portal.fields.lastOdometer"),
        align: "right",
        cell: (row) =>
          row.lastOdometerKm === null ? (
            "—"
          ) : (
            <span className="tabular">{formatCount(row.lastOdometerKm)} km</span>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PortalHeader title={t("portal.summary.title")} description={t("portal.summary.description")} />
      {summary.error ? (
        <Alert tone="danger">{errorMessage(summary.error)}</Alert>
      ) : !summary.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="grid gap-4">
          <section aria-labelledby="portal-credit" className="grid gap-3">
            <h2 id="portal-credit" className="text-base font-semibold">
              {t("portal.summary.credit")}
            </h2>
            {summary.data.credit.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("portal.summary.noCredit")}</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {summary.data.credit.map((credit) => (
                  <Card key={credit.companyName} className="grid gap-3 p-4">
                    <h3 className="text-sm font-semibold">{credit.companyName}</h3>
                    <dl className="grid gap-3 sm:grid-cols-3">
                      <Field label={t("portal.summary.creditLimit")}>
                        {credit.creditLimit === null ? (
                          t("portal.unlimited")
                        ) : (
                          <MoneyText value={credit.creditLimit} />
                        )}
                      </Field>
                      <Field label={t("portal.summary.balance")}>
                        <MoneyText value={credit.balance} />
                      </Field>
                      <Field label={t("portal.summary.available")}>
                        {credit.available === null ? (
                          t("portal.unlimited")
                        ) : (
                          <MoneyText value={credit.available} colorize />
                        )}
                      </Field>
                    </dl>
                  </Card>
                ))}
              </div>
            )}
          </section>
          <Card className="p-4">
            <h2 className="mb-3 text-base font-semibold">{t("portal.summary.usage")}</h2>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Field label={t("portal.summary.today", { day: formatDate(summary.data.usage.day) })}>
                <QuantityText value={summary.data.usage.dayLitres} unit="LITRE" />
              </Field>
              <Field label={t("portal.summary.todayAmount")}>
                <MoneyText value={summary.data.usage.dayAmount} />
              </Field>
              <Field label={t("portal.summary.month", { month: summary.data.usage.month })}>
                <QuantityText value={summary.data.usage.monthLitres} unit="LITRE" />
              </Field>
              <Field label={t("portal.summary.monthAmount")}>
                <MoneyText value={summary.data.usage.monthAmount} />
              </Field>
              <Field label={t("portal.summary.billingCycle")}>
                {FLEET_BILLING_CYCLE_LABELS[summary.data.billingCycle as FleetBillingCycle] ??
                  summary.data.billingCycle}
              </Field>
            </dl>
          </Card>
          <DataTable
            caption={t("portal.summary.vehiclesCaption")}
            columns={columns}
            rows={summary.data.vehicles.filter((vehicle) => vehicle.status === "ACTIVE")}
            rowKey={(row) => row.vehicleId}
            emptyState={
              <EmptyState
                headingLevel={2}
                icon={<Car />}
                title={t("portal.vehicles.emptyTitle")}
                description={t("portal.vehicles.emptyDescription")}
              />
            }
          />
        </div>
      )}
    </>
  );
}
